import { useEffect, useRef } from "react";
import { Button } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

// MUI PACKAGES
import StadiumIcon from "@mui/icons-material/Stadium";
import { BookOpen } from "lucide-react";

import {
  fetchResultsBySession,
  fetchAnswersBySession,
} from "../../Features/Sessions/Actions/SessionsActions";
import Loading from "../Loading";

const resultInstructions = (language) => {
  let instructions = "";
  const englishInstructions = (
    <>
      <h2 className="fs-3 mb-2">
        You have now successfully completed the Pre-Exam Readiness Check
      </h2>
      <p className="text-muted mb-8 space-y-3">
        If you have managed to complete all steps successfully then you should
        not repeat the System Check or the demo exam. Only the most recent
        System Check outcome will be transmitted to FIFA, so if you repeat the
        Pre-Exam Readiness Check and encounter an issue, you may risk being
        rejected from the exam.
      </p>

      <div className="text-muted mb-8">
        <p className="">
          You should only re-attempt the Pre-Exam Readiness Check if:
        </p>
        <ul className="list-disc ml-6 mt-2">
          <li>You failed one of the System Check steps.</li>
          <li>
            You encountered an issue during the demo exam with one of the tools.
          </li>
          <li>
            You need to change something before exam day (see details{" "}
            <a
              href="https://victvs.fifaexams.com/system-check/passed"
              className="text-blue-600 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>
            ).
          </li>
        </ul>
      </div>

      <div className="mb-8">
        <p className="font-bold">Results</p>
        <p className="text-muted">
          The score achieved on the demo exam is not transmitted to FIFA and
          does not have any impact on your ability to access the exam on your
          selected exam day. Click the{" "}
          <span className="font-bold">View Results</span> button to see the
          results of your demo exam.
        </p>
        <p className="text-muted">
          As per the main FIFA exam, The option to View Results will only be
          visible to candidates who have scored less than 75% on the exam.
        </p>
      </div>

      <p className="font-bold mb-8">
        To exit your demo exam, click the Safe Exam Browser power icon in the
        bottom right hand corner, and enter QUIT password QUIT2804.
      </p>
    </>
  );

  const frenchInstructions = (
    <>
      <h2 className="fs-3 mb-2">
        Vous avez réussi le contrôle de préparation à l'examen.
      </h2>
      <p className="text-muted mb-8 space-y-3">
        Si vous avez suivi toutes les étapes avec succès, vous ne devez pas
        repasser le contrôle système ni l'examen blanc. Seul le résultat du
        dernier contrôle système sera transmis à la FIFA. Par conséquent, si
        vous repassez le contrôle de préparation et rencontrez un problème, vous
        risquez d'être refusé(e) à l'examen.
      </p>

      <div className="text-muted mb-8">
        <p className="">
          Vous ne devez repasser le contrôle de préparation que si:
        </p>
        <ul className="list-disc ml-6 mt-2">
          <li>Vous avez échoué à une étape du contrôle système.</li>
          <li>
            Vous avez rencontré un problème avec l'un des outils pendant
            l'examen blanc.
          </li>
          <li>
            Vous devez modifier un élément avant le jour de l'examen (
            <a
              href="https://victvs.fifaexams.com/system-check/passed"
              className="text-blue-600 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              voir les détails ici
            </a>
            ).
          </li>
        </ul>
      </div>

      <div className="mb-8">
        <p className="font-bold">Résultats</p>
        <p className="text-muted">
          Le score obtenu à l'examen blanc n'est pas transmis à la FIFA et n'a
          aucune incidence sur votre accès à l'examen à la date choisie. Cliquez
          sur le bouton <span className="font-bold">View Results</span> pour
          consulter les résultats de votre examen blanc.
        </p>
        <p className="text-muted">
          Selon l'examen principal de la FIFA, l'option de Voir les Résultats ne
          sera visible que pour les candidats ayant obtenu moins de 75 % à
          l'examen.
        </p>
      </div>

      <p className="font-bold mb-8">
        Pour quitter votre examen blanc, cliquez sur l'icône d'alimentation du
        navigateur d'examen sécurisé en bas à droite et saisissez le mot de
        passe QUIT2804.
      </p>
    </>
  );

  const spanishInstructions = (
    <>
      <h2 className="fs-3 mb-2">
        Ha completado con éxito la Comprobación de Preparación Previa al Examen
      </h2>
      <p className="text-muted mb-8 space-y-3">
        Si ha completado todos los pasos correctamente, no debe repetir la
        Comprobación del Sistema ni el examen de demostración. Solo se
        transmitirá a la FIFA el resultado más reciente de la Comprobación del
        Sistema, por lo que si repite la Comprobación de Preparación Previa al
        Examen y encuentra algún problema, podría ser rechazado.
      </p>

      <div className="text-muted mb-8">
        <p className="">
          Solo debe volver a intentar la Comprobación de Preparación Previa al
          Examen si:
        </p>
        <ul className="list-disc ml-6 mt-2">
          <li>Falló uno de los pasos de la Comprobación del Sistema.</li>
          <li>
            Tuvo un problema durante el examen de demostración con alguna de las
            herramientas.
          </li>
          <li>
            Necesita cambiar algo antes del día del examen ({" "}
            <a
              href="https://victvs.fifaexams.com/system-check/passed"
              className="text-blue-600 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              consulte los detalles aquí
            </a>
            ).
          </li>
        </ul>
      </div>

      <div className="mb-8">
        <p className="font-bold">Resultados</p>
        <p className="text-muted">
          La puntuación obtenida en el examen de demostración no se transmite a
          la FIFA y no afecta su acceso al examen el día seleccionado. Haga clic
          en el botón <span className="font-bold">View Results</span> para ver
          los resultados de su examen de demostración.
        </p>
        <p className="text-muted">
          Según el examen principal de la FIFA, la opción de Ver Resultados solo
          será visible para los candidatos que hayan obtenido menos del 75% en
          el examen.
        </p>
      </div>

      <p className="font-bold mb-8">
        Para salir del examen de demostración, haga clic en el icono de
        encendido de Safe Exam Browser en la esquina inferior derecha e
        introduzca la contraseña QUIT2804.
      </p>
    </>
  );

  switch (language) {
    case "en":
      instructions = englishInstructions;
      break;
    case "fr":
      instructions = frenchInstructions;
      break;
    case "es":
      instructions = spanishInstructions;
      break;
    case "de":
      instructions = englishInstructions;
      break;
    default:
      instructions = englishInstructions;
      break;
  }

  return instructions;
};

export default function SessionFinish() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const sessionId = parseInt(id);
  const mounted = useRef(null);

  const navigate = useNavigate();
  const { session, result, loading, error } = useSelector(
    (store) => store.sessions,
  );

  const filterSessionsId = [8, 18, 22, 24];

  const handleNavigate = () => {
    navigate("/candidate/assessments", { replace: true });
  };

  const handleNavigateResults = () => {
    const { esuid } = session;
    navigate(`/candidate/results/${esuid}`, { replace: true });
  };

  useEffect(() => {
    mounted.current = true;
    if (error) return;
    if (mounted.current && !result) {
      dispatch(fetchAnswersBySession(sessionId)).then(() =>
        dispatch(fetchResultsBySession(sessionId)),
      );
    }
    return () => (mounted.current = false);
  }, [error, sessionId]);

  useEffect(() => {
    mounted.current = true;
    if (error) return;
    if (mounted.current) {
      dispatch(fetchResultsBySession(sessionId));
      dispatch(fetchAnswersBySession(sessionId));
      setTimeout(() => fetchResultsBySession(sessionId), 5000);
    }
    return () => (mounted.current = false);
  }, []);

  if (loading) {
    return (
      <section className="h-[300px] flex items-center justify-center">
        <Loading />
      </section>
    );
  }

  const examId = session?.schedule?.exam?.exid;
  const isFinalExam = filterSessionsId.includes(examId);
  const examLanguage = session?.schedule?.language;

  return (
    <div className="box p-12 mb-2">
      {isFinalExam ? (
        <>
          <h2 className="fs-3 mb-2">You have successfully taken the exam</h2>
          <p className="text-muted mb-8">
            Thank you very much for your participation. We are currently
            assessing your results. You will be notified of your exam results
            via email and on the FIFA Agent Platform within the timeline
            established in the FIFA Football Agent Exam Rules. Please stay
            tuned.
          </p>
        </>
      ) : (
        resultInstructions(examLanguage)
      )}
      <div className="flex items-start justify-center gap-2">
        <Button
          variant="dark"
          className="text-white px-4 py-2 inter flex items-start justify-center gap-2"
          onClick={handleNavigate}
        >
          <StadiumIcon fontSize={"small"} />
          Navigate back to your homepage
        </Button>
        {result && (
          // filterSessionsId.includes(session?.schedule?.exam?.exid) && (
          <Button
            disabled={
              session.status !== "finished" && session.status !== "expired"
            }
            variant="dark"
            className="text-white px-4 py-2 inter flex items-center justify-center gap-2"
            onClick={handleNavigateResults}
          >
            <BookOpen size={20} />
            View Results
          </Button>
        )}
      </div>
    </div>
  );
}
